import { addDays, format, parseISO } from 'date-fns';
import clsx from 'clsx';

import { useResolvedTheme } from '../theme';
import type { Plan, PlanBlock, Rhythm, RhythmStatus } from './types';
import { clockLabel, hoursLabel, tint } from './util';

interface Props {
  plan: Plan;
  rhythms: Rhythm[];
  onOpenRhythm: (id: string) => void;
  onOpenBlock: (block: PlanBlock, day: string) => void;
  onOpenSettings: () => void;
}

const STATE_ORDER = { behind: 0, on_track: 1, met: 2 } as const;

const PERIOD_WORD: Record<Rhythm['period'], string> = {
  day: 'this week',
  week: 'this week',
  fortnight: 'these 2 weeks',
  month: 'this month'
};

export default function RhythmRail({ plan, rhythms, onOpenRhythm, onOpenBlock, onOpenSettings }: Props) {
  const theme = useResolvedTheme();
  const byId = new Map(rhythms.map((r) => [r.id, r]));
  const statuses = plan.rhythms
    .filter((s) => byId.has(s.rhythm_id))
    .sort(
      (a, b) =>
        STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
        (byId.get(a.rhythm_id)!.position - byId.get(b.rhythm_id)!.position)
    );

  // Social rhythms with a suggested time in the next four days and nothing
  // booked yet: these need a message to someone before they can happen.
  const horizon = format(addDays(parseISO(plan.today), 4), 'yyyy-MM-dd');
  const reach: { block: PlanBlock; day: string; rhythm: Rhythm }[] = [];
  const seen = new Set<string>();
  for (const d of plan.days) {
    if (d.date < plan.today || d.date > horizon) continue;
    for (const b of d.blocks) {
      const r = byId.get(b.rhythm_id);
      const st = plan.rhythms.find((s) => s.rhythm_id === b.rhythm_id);
      if (!r?.social || b.type !== 'proposal' || seen.has(r.id) || (st && st.booked > 0)) continue;
      seen.add(r.id);
      reach.push({ block: b, day: d.date, rhythm: r });
    }
  }

  const done = statuses.filter((s) => s.state === 'met').length;

  return (
    <aside className="flex w-[300px] flex-shrink-0 flex-col border-l border-[var(--border)] bg-[var(--bg-sunken)]">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {reach.length > 0 && (
          <section className="mb-4">
            <h3 className="mb-2 px-1 text-[0.7rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">
              Reach out
            </h3>
            <div className="flex flex-col gap-[6px]">
              {reach.map(({ block, day, rhythm }) => {
                const t = tint(rhythm.color, theme);
                const s = new Date(block.start);
                return (
                  <button
                    key={rhythm.id}
                    type="button"
                    onClick={() => onOpenBlock(block, day)}
                    className="group flex items-center gap-3 rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-[9px] text-left transition-colors hover:border-[var(--border-strong)]"
                  >
                    <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full" style={{ background: t.soft, color: t.solid }}>
                      <ChatIcon />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[0.86rem] font-medium">{rhythm.name}</span>
                      <span className="block text-[0.74rem] text-[var(--text-muted)]">
                        {format(s, 'EEE')} {clockLabel(s)}
                      </span>
                    </span>
                    <span className="text-[0.75rem] text-[var(--accent)] opacity-0 transition-opacity group-hover:opacity-100">
                      Book
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h3 className="mb-2 flex items-baseline justify-between px-1 text-[0.7rem] font-medium uppercase tracking-wide text-[var(--text-faint)]">
            <span>Rhythms</span>
            <span className="normal-case tracking-normal">
              {done} of {statuses.length} met
            </span>
          </h3>
          <div className="flex flex-col">
            {statuses.map((st) => (
              <RhythmRow
                key={st.rhythm_id}
                status={st}
                rhythm={byId.get(st.rhythm_id)!}
                today={plan.today}
                onOpen={() => onOpenRhythm(st.rhythm_id)}
              />
            ))}
          </div>
          {statuses.length === 0 && (
            <p className="px-1 text-[0.85rem] text-[var(--text-muted)]">No rhythms yet.</p>
          )}
        </section>
      </div>
      <button
        type="button"
        onClick={onOpenSettings}
        className="flex items-center gap-2 border-t border-[var(--border)] px-4 py-3 text-left text-[0.8rem] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
      >
        <MoonIcon />
        <span>
          Sleep {clockLabel(new Date(`2000-01-01T${plan.settings.bed_time}`))} to{' '}
          {clockLabel(new Date(`2000-01-01T${plan.settings.wake_time}`))}
        </span>
      </button>
    </aside>
  );
}

function RhythmRow({
  status,
  rhythm,
  today,
  onOpen
}: {
  status: RhythmStatus;
  rhythm: Rhythm;
  today: string;
  onOpen: () => void;
}) {
  const theme = useResolvedTheme();
  const t = tint(rhythm.color, theme);
  const minutes = status.unit === 'minutes';
  const value = minutes
    ? `${hoursLabel(status.done)} / ${hoursLabel(status.effective)}`
    : `${status.done} / ${status.effective}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-[6px] rounded-lg px-2 py-[9px] text-left transition-colors hover:bg-[var(--bg-hover)]"
    >
      <span className="flex items-center gap-2">
        <span className="h-[10px] w-[10px] flex-shrink-0 rounded-[3px]" style={{ background: t.solid }} />
        <span className="min-w-0 flex-1 truncate text-[0.86rem]">{rhythm.name}</span>
        {status.state === 'met' ? (
          <span className="flex items-center gap-1 text-[0.75rem] text-[var(--text-muted)]">
            <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden>
              <path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke={t.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {value}
          </span>
        ) : (
          <span
            className={clsx(
              'text-[0.75rem] tabular-nums',
              status.state === 'behind' ? 'text-[var(--today-fg)]' : 'text-[var(--text-muted)]'
            )}
          >
            {value}
          </span>
        )}
      </span>
      <span className="pl-[18px]">
        {rhythm.period === 'day' ? (
          <WeekStrip status={status} rhythm={rhythm} today={today} color={t.solid} />
        ) : minutes ? (
          <Bar status={status} color={t.solid} />
        ) : (
          <Pips status={status} color={t.solid} />
        )}
      </span>
      {status.effective < status.target && (
        <span className="pl-[18px] text-[0.7rem] text-[var(--text-faint)]">
          Building toward {minutes ? hoursLabel(status.target) : status.target} {PERIOD_WORD[rhythm.period]}
        </span>
      )}
    </button>
  );
}

function WeekStrip({
  status,
  rhythm,
  today,
  color
}: {
  status: RhythmStatus;
  rhythm: Rhythm;
  today: string;
  color: string;
}) {
  const start = parseISO(status.period_start);
  const done = new Set(status.done_days);
  return (
    <span className="flex gap-[3px]">
      {Array.from({ length: 7 }, (_, i) => {
        const d = addDays(start, i);
        const key = format(d, 'yyyy-MM-dd');
        const allowed = rhythm.days.includes(d.getDay());
        const isDone = done.has(key);
        return (
          <span
            key={key}
            title={format(d, 'EEE d')}
            className={clsx(
              'h-[8px] flex-1 rounded-[2px]',
              !isDone && 'bg-[var(--border)]',
              key === today && !isDone && 'ring-1 ring-[var(--text-faint)]',
              !allowed && 'opacity-30'
            )}
            style={isDone ? { background: color } : undefined}
          />
        );
      })}
    </span>
  );
}

function Pips({ status, color }: { status: RhythmStatus; color: string }) {
  const n = Math.max(status.target, status.done + status.booked);
  if (n > 10) return <Bar status={status} color={color} />;
  return (
    <span className="flex gap-[4px]">
      {Array.from({ length: n }, (_, i) => {
        const kind =
          i < status.done ? 'done' : i < status.done + status.booked ? 'booked' : i < status.effective ? 'todo' : 'stretch';
        return (
          <span
            key={i}
            className={clsx(
              'h-[9px] w-[9px] rounded-full',
              kind === 'todo' && 'border-[1.5px] border-[var(--border-strong)]',
              kind === 'stretch' && 'border border-dashed border-[var(--border-strong)] opacity-60'
            )}
            style={
              kind === 'done'
                ? { background: color }
                : kind === 'booked'
                ? { border: `1.5px solid ${color}` }
                : undefined
            }
          />
        );
      })}
    </span>
  );
}

function Bar({ status, color }: { status: RhythmStatus; color: string }) {
  const max = Math.max(status.target, status.done + status.booked, 1);
  const pct = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  return (
    <span className="relative block h-[6px] overflow-hidden rounded-full bg-[var(--border)]">
      <span
        className="absolute inset-y-0 left-0 rounded-full opacity-35"
        style={{ width: pct(status.done + status.booked), background: color }}
      />
      <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: pct(status.done), background: color }} />
      {status.effective < status.target && (
        <span
          className="absolute inset-y-[-2px] w-[2px] bg-[var(--text-faint)]"
          style={{ left: pct(status.effective) }}
        />
      )}
    </span>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden>
      <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden>
      <path d="M13 9.5A5.5 5.5 0 0 1 6.5 3a5.5 5.5 0 1 0 6.5 6.5z" />
    </svg>
  );
}
